import React, { useState, useMemo, useEffect } from 'react';
import PageThumbnailPreview from '../Editor/PageThumbnailPreview';

/**
 * ReadOnlyCanvas — public, view-only renderer for a design or template.
 *
 * Single rendering source: this component is a thin wrapper around the
 * existing `PageThumbnailPreview` (the same renderer the editor uses for
 * pages-strip thumbnails). It does NOT reimplement element rendering.
 * Text, images, shapes, SVGs, clip paths, gradients, image backgrounds,
 * rotations, filters, and text shadows all flow through that one
 * source so the viewer cannot drift from the editor.
 *
 * Why not extract a smaller pure renderer:
 *   `PageThumbnailPreview` already accepts every render-relevant input
 *   (page, canvasSize, fillWidth). It has no editor coupling — no
 *   selection state, no drag handlers. Wrapping it is strictly less
 *   risky than peeling it apart.
 *
 * Props:
 *   pages              Array<Page>            — required. Same schema as
 *                                                the editor (id, name,
 *                                                elements, background,
 *                                                backgroundConfig, visible).
 *   canvasSize         { width, height }      — required.
 *   currentPageId      string                 — optional. When set, only
 *                                                that page renders. When
 *                                                omitted, every visible
 *                                                page renders stacked.
 *   onPageChange       (pageId) => void       — optional. Receives clicks
 *                                                on the prev/next nav.
 *   showPageNav        bool (default true)    — for designs with > 1 page.
 *   className          string                 — wrapper class.
 *
 * The renderer respects per-page `visible: false` and skips those pages,
 * matching editor behaviour.
 */
export default function ReadOnlyCanvas({
  pages,
  canvasSize,
  currentPageId = null,
  onPageChange = null,
  showPageNav = true,
  className = '',
}) {
  const safePages = useMemo(
    () => (Array.isArray(pages) ? pages.filter(p => p && p.visible !== false) : []),
    [pages]
  );

  const [internalIndex, setInternalIndex] = useState(0);

  // If a specific page is requested, sync the internal index.
  useEffect(() => {
    if (!currentPageId) return;
    const i = safePages.findIndex(p => p.id === currentPageId);
    if (i >= 0) setInternalIndex(i);
  }, [currentPageId, safePages]);

  if (!Array.isArray(pages) || safePages.length === 0 || !canvasSize) {
    return (
      <div
        data-testid="readonly-canvas-empty"
        className={`bg-gray-50 border border-gray-200 rounded-md p-8 text-center text-gray-400 text-sm ${className}`}
      >
        Nothing to preview.
      </div>
    );
  }

  // currentPageId provided OR multi-page nav controls a single visible page.
  const currentPage = safePages[Math.min(internalIndex, safePages.length - 1)];
  const totalPages = safePages.length;
  const multiPage = totalPages > 1;

  const goTo = (i) => {
    const clamped = Math.max(0, Math.min(totalPages - 1, i));
    setInternalIndex(clamped);
    onPageChange && onPageChange(safePages[clamped].id);
  };

  return (
    <div
      data-testid="readonly-canvas"
      className={`w-full ${className}`}
    >
      <div
        className="w-full bg-white border border-gray-200 rounded-md shadow-sm overflow-hidden"
        data-testid="readonly-canvas-page"
        data-page-id={currentPage.id}
      >
        <PageThumbnailPreview
          page={currentPage}
          canvasSize={canvasSize}
          fillWidth
        />
      </div>

      {multiPage && showPageNav && (
        <div
          className="flex items-center justify-center gap-3 mt-3 text-sm text-gray-600"
          data-testid="readonly-canvas-pagenav"
        >
          <button
            type="button"
            onClick={() => goTo(internalIndex - 1)}
            disabled={internalIndex === 0}
            className="px-3 py-1 rounded border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
            data-testid="readonly-canvas-prev"
            aria-label="Previous page"
          >
            ‹ Prev
          </button>
          <span data-testid="readonly-canvas-counter">
            Page {internalIndex + 1} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => goTo(internalIndex + 1)}
            disabled={internalIndex >= totalPages - 1}
            className="px-3 py-1 rounded border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
            data-testid="readonly-canvas-next"
            aria-label="Next page"
          >
            Next ›
          </button>
        </div>
      )}
    </div>
  );
}
